import { reactiveFeedbackMessage } from 'core/apollo-cache';
import * as React from 'react';

// Local Imports
import {
  Button,
  Loader,
  Panel,
  PanelBody,
  PanelFooter,
  PanelHeader,
  PanelRow,
} from '@teamsnap/teamsnap-ui';
import Modal from 'components/common/modal';
import {
  LoadingType,
  useOrderDetails,
  useOrderDetailsActions,
} from 'hooks/orderDetails';
import '../modal-styles.scss';

type Props = {
  isOpen: boolean;
  closeFn: () => void;
  installment?: any;
  orderId: number;
};

export const CancelInstallmentModal: React.FC<Props> = ({
  isOpen,
  closeFn,
  installment,
  orderId,
}) => {
  const { loading } = useOrderDetails();
  const { updateInstallment, getInstallments } = useOrderDetailsActions();

  const isLoading = loading[LoadingType.UpdateInstallment];

  const handleSubmit = async () => {
    try {
      const response = await updateInstallment(installment.id, true);

      if (response?.id) {
        reactiveFeedbackMessage({
          message: `Success! Installment ${installment.id} has been cancelled.`,
          autoclose: true,
          isError: false,
        });

        getInstallments(orderId);
        closeFn();
      } else {
        reactiveFeedbackMessage({
          message: `Unable to cancel Installment ${installment.id}.`,
          autoclose: true,
          isError: true,
        });
      }
    } catch (error) {
      reactiveFeedbackMessage({
        message: `Oops, something went wrong! Unable to cancel Installment ${installment.id}.`,
        autoclose: false,
        isError: true,
      });
    }
  };
  if (!installment) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={closeFn}>
      <Panel
        className="edit-installment-modal"
        testId="cancel-installment-modal--container"
      >
        <PanelHeader
          className="Panel-header"
          title="Cancel Scheduled Payment"
          testId="cancel-installment-modal--container-header"
        />
        <PanelBody
          className="Panel-body"
          testId="cancel-installment-modal--container-body"
        >
          <PanelRow testId="cancel-installment-modal--container-row">
            <div
              className="u-padXs u-spaceEndsXs "
              data-testid="cancel-installment-modal--oustanding-balance-msg"
            >
              Canceling this installment leaves an oustanding balance of{' '}
              {installment.amount}. To change the balance owed, adjust the
              invoice amount.
            </div>
          </PanelRow>
        </PanelBody>
        <PanelFooter
          mods="u-text-left"
          testId="cancel-installment-modal--container-footer"
        >
          <div className="button-list">
            <Button
              isDisabled={isLoading}
              color="default"
              onClick={() => closeFn()}
              testId="cancel-installment-modal--cancel-Button"
            >
              Cancel
            </Button>

            <Button
              isDisabled={isLoading}
              mods="Button--negative"
              type="submit"
              onClick={() => handleSubmit()}
              testId="cancel-installment-modal--yes-button"
            >
              {isLoading && (
                <Loader type="spin" style={{ width: '13px', height: '13px' }} />
              )}
              {!isLoading && `Yes, cancel ${installment.amount} payment`}
            </Button>
          </div>
        </PanelFooter>
      </Panel>
    </Modal>
  );
};
